import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import {
  CURRICULA_WITH_NGSS_DIMENSION_READTHROUGH,
  IDAHO,
  NGSS,
  OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS,
  PERFORMANCE_EXPECTATION_CCC,
  PERFORMANCE_EXPECTATION_DCI,
  PERFORMANCE_EXPECTATION_ENG,
  PERFORMANCE_EXPECTATION_NOS,
  PERFORMANCE_EXPECTATION_SEP,
  CURRICULUM_OPTIONS_LABELS_US,
} from 'global/constants';
import {
  OptionWithKey,
  OutcomeOption,
} from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes.constants';
import {
  Outcome,
  OutcomeGroup,
  PERFORMANCE_EXPECTATION_ENG_LABEL,
} from './outcome.constants';

export function getLabelOptionFormat(outcome: Outcome) {
  if (outcome.category === OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS) {
    return {
      label: outcome.code,
      description: outcome.description,
    };
  } else if (outcome.category === PERFORMANCE_EXPECTATION_DCI) {
    return {
      label: outcome.nameCode
        ? `${outcome.nameCode}: ${outcome.name}`
        : outcome.name || outcome.code,
      description: outcome.description,
    };
  } else if (
    [
      PERFORMANCE_EXPECTATION_SEP,
      PERFORMANCE_EXPECTATION_CCC,
      PERFORMANCE_EXPECTATION_ENG,
      PERFORMANCE_EXPECTATION_NOS,
    ].includes(outcome.category || '')
  ) {
    return {
      label: outcome.nameCode
        ? `${outcome.nameCode}: ${outcome.name}`
        : outcome.name || outcome.code,
      description: outcome.description,
    };
  } else {
    return {
      label: `${outcome.code}${outcome.version ? ` (${outcome.version})` : ''}`,
      description: outcome.description,
    };
  }
}

export function createCurriculumGroups(
  curriculum: string,
  outcomes: Outcome[]
) {
  const groups: {
    [key: string]: OutcomeGroup;
  } = {};
  outcomes.forEach(o => {
    if (o.version) {
      const key = `${curriculum}${o.version}`;
      if (groups[key]) {
        groups[key].curriculum = curriculum;
        groups[key].version = o.version;
        groups[key].outcomes.push(o);
      } else {
        groups[key] = { key, curriculum, version: o.version, outcomes: [o] };
      }
    } else if (o.metaInfo) {
      const key = `${curriculum}${o.metaInfo}`;
      if (groups[key]) {
        groups[key].curriculum = curriculum;
        groups[key].subLabel = o.metaInfo;
        groups[key].outcomes.push(o);
      } else {
        groups[key] = { key, curriculum, subLabel: o.metaInfo, outcomes: [o] };
      }
    } else {
      if (groups[curriculum]) {
        groups[curriculum].curriculum = curriculum;
        groups[curriculum].outcomes.push(o);
      } else {
        groups[curriculum] = { key: curriculum, curriculum, outcomes: [o] };
      }
    }
  });
  return groups;
}

export const groupOutcomesByCurriculum = (
  outcomes: Outcome[] | null,
  curriculums?: string[]
) => {
  let hasMoreToShow = false;
  const outcomeGroups: OutcomeGroup[] = [];

  const groups = groupBy(
    curriculums
      ? outcomes?.filter(o => curriculums.includes(o.curriculum))
      : outcomes,
    'curriculum'
  );

  for (const curriculum in groups) {
    const curriculumGroup = createCurriculumGroups(
      curriculum,
      groups[curriculum]
    );
    for (const key in curriculumGroup) {
      if (curriculumGroup[key].outcomes.length > 4) {
        hasMoreToShow = true;
      }
      outcomeGroups.push({
        ...curriculumGroup[key],
        outcomes: sortBy(curriculumGroup[key].outcomes, 'code'),
      });
    }
  }
  return {
    outcomeGroups: sortBy(outcomeGroups, ['key']),
    hasMoreToShow,
  };
};

export const mapOutcomeToOption = (outcome: Outcome): OutcomeOption => {
  const { label, description } = getLabelOptionFormat(outcome);
  return {
    label,
    description,
    value: outcome.id,
    version: outcome.version,
    groupLabel: outcome.groupLabel || null,
    name: outcome.name,
    nameCode: outcome.nameCode,
    dciName: outcome.dciName,
    dciCode: outcome.dciCode,
    category: outcome.category,
  };
};

export const mapOutcomesToOptions = (
  groupByField: string,
  outcomes: Outcome[],
  savedOutcomes?: Outcome[]
) => {
  const outcomeGroups = groupBy(outcomes, groupByField);
  const outcomeOptions: { [key: string]: OptionWithKey[] } = {};

  for (let k in outcomeGroups) {
    outcomeOptions[k] = outcomeGroups[k].map(outcome => ({
      ...mapOutcomeToOption(outcome),
      key: k,
      alreadyAdded:
        savedOutcomes &&
        savedOutcomes.findIndex(saved => saved.id === outcome.id) !== -1
          ? true
          : false,
    }));
  }
  return outcomeOptions;
};

export const flattenObjectToArrayWithKey = (object: {
  [key: string]: OptionWithKey[];
}) => {
  const items: OptionWithKey[] = [];
  Object.keys(object).forEach(key => {
    items.push(
      ...object[key].map(item => {
        return { ...item, key };
      })
    );
  });
  return items;
};

export function getCurriculumDisplayLabel(curriculum: string): string {
  return (
    CURRICULUM_OPTIONS_LABELS_US[
      curriculum as keyof typeof CURRICULUM_OPTIONS_LABELS_US
    ] ?? curriculum
  );
}

// Some curricula label their performance expectations differently in the UI
// (e.g. Idaho officially calls them "Content Standards"). This is a display-only
// concern — the underlying outcome still has the canonical PE type — so the
// wording lives here rather than being baked into the outcome's `type` column.
const PE_TYPE_LABEL_BY_CURRICULUM: Record<string, string> = {
  [IDAHO]: 'Content Standards',
};

export function getPerformanceExpectationTypeLabel(outcome: Outcome): string {
  if (
    outcome.type === OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS ||
    !outcome.type
  ) {
    return getPerformanceExpectationTypeLabelByCurriculum(outcome.curriculum);
  }
  return outcome.type;
}

// The performance-expectation display label for a curriculum, keyed only on the
// curriculum code. Used where a full Outcome isn't available (e.g. the planner
// kanban card's grouped outcomes) but the label must still match the badge shown
// elsewhere — Idaho → "Content Standards", others → "Performance Expectations".
export function getPerformanceExpectationTypeLabelByCurriculum(
  curriculum: string
): string {
  return PE_TYPE_LABEL_BY_CURRICULUM[curriculum] ?? 'Performance Expectations';
}

// The NGSS dimension categories that are universal (a property of the content,
// not the state) and so are read through onto NGSS-adopting state curricula.
// Scoped to SEP/CCC per TT-1287; ENG/NOS are excluded until there is concrete
// evidence they should surface under a state lens. PE and DCI are genuinely
// per-state and are never read through.
const NGSS_READTHROUGH_DIMENSION_CATEGORIES = [
  PERFORMANCE_EXPECTATION_SEP,
  PERFORMANCE_EXPECTATION_CCC,
];

/**
 * Returns the content's existing NGSS SEP/CCC outcomes so they can be displayed
 * under a state curriculum's lens (e.g. Idaho), rather than duplicating those
 * universal NGSS dimensions per-state. Returns an empty array for curricula not
 * enabled for read-through (NGSS itself, ISTE, or non-NGSS state frameworks).
 * See CURRICULA_WITH_NGSS_DIMENSION_READTHROUGH / TT-1287.
 */
export function getNgssDimensionReadThroughOutcomes<
  T extends Pick<Outcome, 'curriculum' | 'category'>,
>(outcomes: T[], curriculum: string): T[] {
  if (!CURRICULA_WITH_NGSS_DIMENSION_READTHROUGH.includes(curriculum)) {
    return [];
  }
  return outcomes.filter(
    o =>
      o.curriculum === NGSS &&
      NGSS_READTHROUGH_DIMENSION_CATEGORIES.includes(o.category)
  );
}

export function getPerformanceExpectationBadgeTitle(outcome: Outcome) {
  return outcome.category === PERFORMANCE_EXPECTATION_ENG
    ? PERFORMANCE_EXPECTATION_ENG_LABEL
    : outcome.category === OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS
      ? `${getCurriculumDisplayLabel(
          outcome.curriculum
        )} ${getPerformanceExpectationTypeLabel(outcome)}`
      : outcome.groupLabel || '';
}

export function getPerformanceExpectationTitle(outcome: Outcome) {
  return outcome.category === PERFORMANCE_EXPECTATION_DCI
    ? `${outcome.nameCode}: ${outcome.name}`
    : outcome.name || outcome.code;
}
